import React, { useState, useRef, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  animations,
  styles,
  SectionHeader,
} from "../../../components/landing/SharedComponent";

const ServiceDetail = ({
  service,
  services,
  isOpen,
  onClose,
  onServiceSwitch,
}) => {
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [currentService, setCurrentService] = useState(service);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const galleryRef = useRef(null);
  const modalContentRef = useRef(null);

  useEffect(() => {
    if (service) {
      setCurrentService(service);
    }
  }, [service]);

  const modalVariants = {
    initial: { opacity: 0, y: 100 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        type: "spring",
        damping: 25,
      },
    },
    exit: {
      opacity: 0,
      y: 100,
      transition: { duration: 0.3 },
    },
  };

  const contentVariants = {
    initial: { opacity: 0, y: -100 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: 100,
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const scrollGallery = (direction) => {
    if (galleryRef.current) {
      const scrollAmount = direction === "right" ? 320 : -320;
      galleryRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  if (!currentService) return null;

  const otherServices = services.filter((s) => s.id !== currentService.id);

  const handleServiceChange = (newService) => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setCurrentService(newService);
      onServiceSwitch(newService);
      setIsTransitioning(false);
      setActiveGalleryIndex(0);

      if (modalContentRef.current) {
        const scrollContainer =
          modalContentRef.current.querySelector(".overflow-y-auto");
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    }, 50);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={handleOverlayClick}
        >
          <motion.div
            ref={modalContentRef}
            variants={modalVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative w-[1000px] h-[calc(100vh-0px)] my-5"
          >
            <button
              onClick={onClose}
              className={`absolute top-4 right-4 z-20 bg-black/40 p-2 rounded-full hover:bg-black/90 ${styles.transitionBase}`}
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="h-full overflow-y-auto bg-white scrollbar-hide">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentService.id}
                  variants={contentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <div
                    className="relative h-[60vh] bg-cover bg-center flex items-center justify-center"
                    style={{
                      backgroundImage: `url(${currentService.mainImage})`,
                    }}
                  >
                    <div className={`${styles.overlay} bg-black/75`} />
                    <div className="relative z-10 text-center px-4">
                      <h1 className="text-5xl font-bold text-white mb-4">
                        {currentService.title}
                      </h1>
                      <p className="text-xl text-white/90">
                        {currentService.description}
                      </p>
                    </div>
                  </div>

                  <motion.div
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                    variants={animations.fadeInUp}
                    className="py-12 bg-white"
                  >
                    <div className={`max-w-3xl mx-auto px-4`}>
                      <SectionHeader
                        title="About This Service"
                        className="text-left mb-6"
                        size="md"
                      />
                      <p className="text-gray-700 leading-relaxed text-lg mb-8">
                        {currentService.extendedDetails}
                      </p>

                      <div className="grid md:grid-cols-2 gap-5">
                        {currentService.features.map((feature, idx) => (
                          <motion.div
                            key={idx}
                            variants={animations.scaleIn}
                            className={`flex items-start gap-4 bg-gray-50 p-5 rounded-lg ${styles.shadowBase}`}
                          >
                            <div className="w-2 h-2 mt-2 bg-primary rounded-full" />
                            <span className="text-gray-700 text-lg">
                              {feature}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>

                  {/* Service Gallery */}
                  <motion.div
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                    variants={animations.fadeInUp}
                    className="py-12 bg-gray-50"
                  >
                    <div className="px-0">
                      <SectionHeader
                        title="Service Gallery"
                        className="text-left mb-6 max-w-3xl mx-auto"
                        size="md"
                      />

                      {/* Gallery container */}
                      <div className="relative">
                        {/* Desktop navigation buttons */}
                        <div className="hidden md:block">
                          <button
                            onClick={() => scrollGallery("left")}
                            className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 
                              bg-white/80 hover:bg-white p-2 rounded-full ${styles.shadowBase} ${styles.transitionBase}`}
                            aria-label="Previous image"
                          >
                            <ChevronLeft className="w-6 h-6 text-primary" />
                          </button>
                          <button
                            onClick={() => scrollGallery("right")}
                            className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 
                              bg-white/80 hover:bg-white p-2 rounded-full ${styles.shadowBase} ${styles.transitionBase}`}
                            aria-label="Next image"
                          >
                            <ChevronRight className="w-6 h-6 text-primary" />
                          </button>
                        </div>

                        <div
                          ref={galleryRef}
                          className="flex overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
                          onScroll={(e) => {
                            if (!galleryRef.current) return;
                            const scrollLeft = galleryRef.current.scrollLeft;
                            const width = galleryRef.current.clientWidth;
                            const scrollWidth = galleryRef.current.scrollWidth;
                            const index = Math.round(
                              (scrollLeft / (scrollWidth - width)) *
                                (currentService.gallery.length - 1)
                            );
                            setActiveGalleryIndex(index);
                          }}
                        >
                          {currentService.gallery.map((image, idx) => (
                            <motion.div
                              key={idx}
                              variants={animations.scaleIn}
                              className="flex-none w-full aspect-video snap-center overflow-hidden"
                            >
                              <img
                                src={image}
                                alt={`${currentService.title} gallery ${
                                  idx + 1
                                }`}
                                className={`w-full h-full object-cover hover:scale-105 ${styles.transitionSlow}`}
                                loading="lazy"
                              />
                            </motion.div>
                          ))}
                        </div>

                        {/* Dots indicator for mobile */}
                        <div className="flex justify-center mt-4 md:hidden">
                          {currentService.gallery.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                setActiveGalleryIndex(idx);
                                if (galleryRef.current) {
                                  const scrollWidth =
                                    galleryRef.current.scrollWidth -
                                    galleryRef.current.clientWidth;
                                  const position =
                                    (idx /
                                      (currentService.gallery.length - 1)) *
                                    scrollWidth;
                                  galleryRef.current.scrollTo({
                                    left: position,
                                    behavior: "smooth",
                                  });
                                }
                              }}
                              className={`w-2 h-2 mx-1 rounded-full ${
                                activeGalleryIndex === idx
                                  ? "bg-primary"
                                  : "bg-gray-300"
                              }`}
                              aria-label={`Go to image ${idx + 1}`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Other Services Section */}
                  <motion.div
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                    variants={animations.fadeInUp}
                    className="py-12 bg-white"
                  >
                    <div className="max-w-4xl mx-auto px-4">
                      <SectionHeader
                        title="Other Services"
                        className="text-left mb-6"
                        size="md"
                      />
                      <motion.div
                        variants={animations.staggerChildren}
                        className="grid md:grid-cols-2 gap-6"
                      >
                        {otherServices.map((otherService) => (
                          <motion.div
                            key={otherService.id}
                            variants={animations.slideIn}
                            className="group cursor-pointer"
                            onClick={() => handleServiceChange(otherService)}
                          >
                            <div
                              className={`relative h-56 rounded-lg overflow-hidden ${styles.shadowLarge}`}
                            >
                              <img
                                src={otherService.mainImage}
                                alt={otherService.title}
                                className={`w-full h-full object-cover group-hover:scale-105 ${styles.transitionSlow}`}
                              />
                              <div
                                className={`${styles.overlay} bg-black/60 flex items-center justify-center group-hover:bg-black/70 ${styles.transitionBase}`}
                              >
                                <div className="text-center p-5">
                                  <div className="text-3xl mb-3 text-white">
                                    {otherService.icon}
                                  </div>
                                  <h3 className="text-xl font-semibold text-white">
                                    {otherService.title}
                                  </h3>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    </div>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ServiceDetail;
